interface ReferralTermsClause {
  number: string;
  text: string;
  items?: string[];
}

interface ReferralTermsSection {
  title: string;
  clauses: ReferralTermsClause[];
}

// Supplied Referral Rewards Program terms, effective 01/06/2026.
export const referralTerms: ReferralTermsSection[] = [
  {
    title: 'Eligibility',
    clauses: [
      { number: '1.1', text: 'Participation in the Program is open only to registered users of Locumspace who are verified healthcare professionals on the platform.' },
      { number: '1.2', text: 'Participants must comply with all LocumSpace platform policies, verification requirements, and applicable laws and regulations.' },
      { number: '1.3', text: 'Employees, contractors, affiliates, partners, agents, or representatives of Locumspace may be excluded from participation at the sole discretion of LocumSpace.' },
    ],
  },
  {
    title: 'Qualifying Referrals',
    clauses: [
      {
        number: '2.1', text: 'Referral points shall only be awarded where:',
        items: [
          'A referred individual registers through a valid referral link or code assigned by Locumspace,',
          'The referred individual is a genuine healthcare worker,',
          'The referred individual successfully completes all verification requirements, and',
          'The referred individual attains Tier 3 user status on the platform.',
        ],
      },
      { number: '2.2', text: 'Referrals that do not meet all conditions stated above shall not qualify for points, rewards, or prizes.' },
      { number: '2.3', text: 'Referrals from individuals who are not healthcare workers shall be deemed invalid and excluded from the Program.' },
    ],
  },
  {
    title: 'Fraudulent, Abusive, or Manipulative Conduct',
    clauses: [
      {
        number: '3.1', text: 'The following actions are strictly prohibited:',
        items: [
          'Creating or operating multiple accounts or email addresses for the benefit of one individual,',
          'Self-referrals,',
          'Fake, misleading, automated, or fraudulent registrations,',
          'Misrepresentation of identity or professional credentials,',
          'Manipulation of referral activity or platform systems,',
          'Any conduct deemed abusive, dishonest, unethical, or inconsistent with the spirit of the Program.',
        ],
      },
      { number: '3.2', text: 'Locumspace reserves the absolute right to investigate suspicious activity and disqualify any participant, referral, or account at any time without prior notice.' },
      { number: '3.3', text: 'Any points, rankings, rewards, or prizes obtained through prohibited conduct may be revoked, withheld, cancelled, or recovered by Locumspace.' },
    ],
  },
  {
    title: 'Rewards and Prize Structure',
    clauses: [
      { number: '4.1', text: 'Eligible participants may receive rewards including cash prizes, data rewards, gadgets, electronics, accessories, or other items determined by Locumspace.' },
      {
        number: '4.2', text: 'Prize categories may include, but are not limited to:',
        items: [
          'Cash rewards,', 'Earbuds or earpods,', 'Power banks,', 'Headphones,',
          'Smart watches,', 'Data subscriptions,', 'Other promotional rewards.',
        ],
      },
      { number: '4.3', text: 'The total prize pool may be valued at up to ₦1,000,000 or any amount determined by Locumspace.' },
      { number: '4.4', text: 'Rewards are non-transferable, non-exchangeable, and may not be redeemed for alternative items except where expressly approved by Locumspace.' },
    ],
  },
  {
    title: 'Determination of Winners',
    clauses: [
      { number: '5.1', text: 'Winners shall be determined based on qualifying referral points accumulated within the applicable monthly campaign period.' },
      {
        number: '5.2', text: 'Locumspace reserves the unrestricted and final right to:',
        items: [
          'Determine eligibility,',
          'Validate referrals and referral points,',
          'Verify compliance with these Terms,',
          'Select, approve, reject, suspend, or disqualify participants,',
          'Decide whether any reward or prize shall be issued,',
          'Withhold or decline any reward where misconduct, suspicious activity, policy violations, or disputes are identified.',
        ],
      },
      { number: '5.3', text: 'All decisions made by Locumspace relating to the Program, rankings, eligibility, verification, qualification, rewards, or disqualification shall be final and binding.' },
      { number: '5.4', text: 'LocumSpace reserves the absolute right to suspend, modify, discontinue, terminate, or cancel the Referral Rewards Program at any time, with or without notice, and without liability or obligation to any participant, including where fraud, abuse, operational issues, regulatory concerns, or business considerations arise.' },
    ],
  },
  {
    title: 'Verification and Award Collection',
    clauses: [
      { number: '6.1', text: 'Participants may be required to provide valid identification, professional credentials, contact information, or other verification documents before receiving rewards.' },
      { number: '6.2', text: 'Failure to provide requested information within the specified timeframe may result in forfeiture of the reward.' },
      { number: '6.3', text: 'Locumspace reserves the right to substitute any advertised reward with another reward of similar value without liability.' },
    ],
  },
  {
    title: 'Modification, Suspension, or Termination',
    clauses: [
      { number: '7.1', text: 'Locumspace reserves the right to modify, suspend, discontinue, or terminate the Program, its rules, rewards, eligibility requirements, or timelines at any time and without prior notice.' },
      { number: '7.2', text: 'Locumspace shall not be liable for any loss, disappointment, damages, or claims arising from changes to or cancellation of the Program.' },
    ],
  },
  {
    title: 'Limitation of Liability',
    clauses: [
      {
        number: '8.1', text: 'By participating in the Program, participants agree that Locumspace shall not be liable for:',
        items: [
          'Technical failures or interruptions,',
          'Delayed, lost, incomplete, or invalid referrals,',
          'Unauthorized access or fraudulent activity,',
          'Errors in rankings or point calculations,',
          'Any indirect, incidental, or consequential damages connected with participation in the Program.',
        ],
      },
    ],
  },
  {
    title: 'Privacy and Data Use',
    clauses: [
      { number: '9.1', text: 'Participants consent to the collection, processing, and use of their data in connection with the administration of the Program in accordance with Locumspace policies and applicable laws.' },
      { number: '9.2', text: 'Locumspace may publish participant names, usernames, referral achievements, or winner announcements for promotional and marketing purposes.' },
    ],
  },
  {
    title: 'Acceptance of Terms',
    clauses: [
      { number: '10.1', text: 'Participation in the Program constitutes full acceptance of these Terms and Conditions.' },
      { number: '10.2', text: 'Any breach of these Terms may result in immediate disqualification and forfeiture of all referral points or rewards.' },
    ],
  },
  {
    title: 'Governing Law',
    clauses: [
      { number: '11.1', text: 'These Terms and Conditions shall be governed by and interpreted in accordance with the laws of the Federal Republic of Nigeria.' },
      { number: '11.2', text: 'Any disputes arising from the Program shall be subject to the exclusive jurisdiction of competent courts in Nigeria.' },
    ],
  },
];
